import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { useTranslation } from 'react-i18next';
import TaskApiService from '../../api/TaskApiService';
import { useRefetchTrigger } from '../../hooks/useRefetchTrigger';

const taskApiService = new TaskApiService();

const COLORS = ['#83a6ed', '#8dd1e1', '#82ca9d', '#a4de6c', '#d88487', '#d0ed57', '#ffc658', '#ffa726'];

const parseSettings = (settings) => {
    try {
        if (!settings || settings === '{}') {
            return {};
        }

        return typeof settings === 'string' ? JSON.parse(settings) : settings;
    }
    catch (error) {
        console.error('Error parsing jsonSettings:', error);
        return {};
    }
};

const DashboardTaskStatus = ({ jsonSettings }) => {
    const { t } = useTranslation();
    const refetchTrigger = useRefetchTrigger();
    const [statuses, setStatuses] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const settings = parseSettings(jsonSettings);
    const excludedStatusIds = Array.isArray(settings.excludedStatusIds) ? settings.excludedStatusIds : [];

    useEffect(() => {
        let ignore = false;

        setLoading(true);

        Promise.all([
            taskApiService.getTaskStatuses(),
            taskApiService.getTaskByStatus(excludedStatusIds)
        ])
            .then(([taskStatuses, taskByStatus]) => {
                if (ignore) {
                    return;
                }

                setStatuses(taskStatuses);
                setTasks(taskByStatus);
            })
            .finally(() => {
                if (!ignore) {
                    setLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jsonSettings, refetchTrigger]);

    const statusLookup = useMemo(() => {
        return new Map(statuses.map((status) => [status.stId, status.localizedName ?? status.stName]));
    }, [statuses]);

    const chartData = useMemo(() => {
        return tasks
            .map((entry, index) => {
                const count = Number(entry.tasks ?? 0);
                return {
                    ...entry,
                    tasks: count,
                    name: statusLookup.get(entry.statusId) ?? entry.statusId,
                    color: COLORS[index % COLORS.length],
                };
            })
            .filter((entry) => entry.tasks > 0)
            .sort((a, b) => b.tasks - a.tasks);
    }, [tasks, statusLookup]);

    const totalTasks = useMemo(() => chartData.reduce((total, entry) => total + entry.tasks, 0), [chartData]);
    const largestStatus = chartData[0];
    const activeStatuses = chartData.length;

    const tooltipFormatter = (value, _name, item) => {
        const count = Number(value ?? 0);
        const percent = totalTasks > 0 ? Math.round((count / totalTasks) * 100) : 0;
        return [`${count} (${percent}%)`, item?.payload?.name ?? ''];
    };

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                p: 1.75,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                background: 'transparent',
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.1, color: '#111827' }}>
                        {t('dashboardTaskStatus.title')}
                    </Typography>
                </Box>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexShrink: 0 }}>
                <MetricBox label={t('dashboardTaskStatus.metric.total')} value={totalTasks} loading={loading} />
                <MetricBox label={t('dashboardTaskStatus.metric.statuses')} value={activeStatuses} loading={loading} />
                <MetricBox
                    label={t('dashboardTaskStatus.metric.largest')}
                    value={largestStatus?.tasks ?? 0}
                    detail={largestStatus?.name}
                    loading={loading}
                />
            </Stack>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(210px, 0.9fr) minmax(180px, 1fr)' },
                    gap: 1.5,
                    alignItems: 'center',
                }}
            >
                {loading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ gridColumn: '1 / -1', height: '100%' }}>
                        <CircularProgress size={30} sx={{ color: '#2563eb' }} />
                    </Stack>
                ) : totalTasks === 0 ? (
                    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ gridColumn: '1 / -1', height: '100%', color: '#64748b' }}>
                        <TaskAltRoundedIcon sx={{ fontSize: 34, color: '#cbd5e1' }} />
                        <Typography variant="body2" fontWeight={700}>
                            {t('dashboardTaskStatus.empty.noTasks')}
                        </Typography>
                    </Stack>
                ) : (
                    <>
                        <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 210 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        dataKey="tasks"
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius="58%"
                                        outerRadius="82%"
                                        paddingAngle={2}
                                        stroke="#ffffff"
                                        strokeWidth={3}
                                    >
                                        {chartData.map((entry) => (
                                            <Cell key={`cell-${entry.statusId}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip formatter={tooltipFormatter} />
                                </PieChart>
                            </ResponsiveContainer>
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    pointerEvents: 'none',
                                }}
                            >
                                <Typography sx={{ fontSize: 30, lineHeight: 1, color: '#111827', fontWeight: 900 }}>
                                    {totalTasks}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                    {t('dashboardTaskStatus.center.total')}
                                </Typography>
                            </Stack>
                        </Box>

                        <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%' }}>
                            {chartData.map((entry) => {
                                const percent = totalTasks > 0 ? Math.round((entry.tasks / totalTasks) * 100) : 0;

                                return (
                                    <Box key={entry.statusId} sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: entry.color, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: 12, color: '#1f2937', fontWeight: 750 }} noWrap>
                                                    {entry.name}
                                                </Typography>
                                            </Stack>
                                            <Box sx={{ mt: 0.4, height: 6, borderRadius: 1, backgroundColor: '#e5e7eb', overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${percent}%`, backgroundColor: entry.color, borderRadius: 1 }} />
                                            </Box>
                                        </Box>
                                        <Stack alignItems="flex-end" sx={{ minWidth: 46 }}>
                                            <Typography sx={{ fontSize: 13, color: '#111827', fontWeight: 900, lineHeight: 1 }}>
                                                {entry.tasks}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 700 }}>
                                                {percent}%
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </Box>
        </Box>
    );
};

function MetricBox({ label, value, detail, loading }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: 1,
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                px: 1,
                py: 0.85,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            }}
        >
            <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }} noWrap>
                {label}
            </Typography>
            {loading ? (
                <Box sx={{ mt: 0.6, height: 24, width: 42, borderRadius: 1, backgroundColor: '#e5e7eb' }} />
            ) : (
                <>
                    <Typography sx={{ fontSize: 23, lineHeight: 1, color: '#111827', fontWeight: 900 }}>
                        {value}
                    </Typography>
                    {detail && (
                        <Typography sx={{ mt: 0.25, fontSize: 11, color: '#64748b', fontWeight: 700 }} noWrap>
                            {detail}
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}

export default DashboardTaskStatus;
